<template>
	<BasePlaceHolder :placehold="loading" :section-count="2" :class="{ 'container py-200': loading }">
		<VentureCapitalAboutTheFund
			theme="light"
			:show-sectors="false"
			:stats="ventureImportantStats"
			:disclaimer="ventureFundDisclaimer"
		/>
		<div v-if="literatureItems.length" class="container py-200 py-400-md">
			<h2 class="heading-4 mb-50 mb-150">Literature</h2>
			<LiteratureSection
				:literature-list="literatureItems"
				:disclaimer="literatureDisclaimer"
				disclaimer-classes="body-lg-md"
			/>
		</div>
	</BasePlaceHolder>
</template>

<script setup lang="ts">
import { Ref, ref } from 'vue';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import { FundLiterature } from 'types/funds';
import { getFund } from '@sanity/proxy/funds';
import { growthTechReitId } from '@constants/reit';
import LiteratureSection from '@components/offering-details/literature-section.vue';
import { percentage } from '@filters/shared-filters';
import { PERFORMANCE_TIMEZONE } from '@constants/timezones';
import { SanityRichTextBlock } from 'types/sanity';
import spacetime from 'spacetime';
import VentureCapitalAboutTheFund from '@components/marketing-site/venture-capital/venture-capital-about-the-fund.vue';
import { VentureImportantStat } from 'types/sanity/venture-marketing';

const loading: Ref<boolean> = ref(true);

const ventureImportantStats: Ref<Array<VentureImportantStat> | null> = ref(null);
const ventureFundDisclaimer: Ref<string | null> = ref(null);
const literatureItems: Ref<Array<FundLiterature>> = ref([]);
const literatureDisclaimer: Ref<Array<SanityRichTextBlock>> = ref([]);

async function setup() {
	try {
		const fundData = await getFund(growthTechReitId, { include: ['HISTORICAL_RETURNS'] });
		if (fundData) {
			literatureItems.value = fundData.literature ?? [];
			literatureDisclaimer.value = fundData.literatureDisclaimer ?? [];

			// build custom stats
			const lastYear = spacetime.now(PERFORMANCE_TIMEZONE).subtract(1, 'year').year().toString();
			const lastYearReturn = fundData.historicalPerformanceReturns!.find((r) => r.year === lastYear);
			if (lastYearReturn && fundData.fundAum) {
				const currentDate = spacetime().format(CustomSpacetimeFormat.MONTH_DATE_YEAR);
				ventureImportantStats.value = [
					{
						label: `${lastYear} Total return`,
						value: percentage(lastYearReturn.months.pop()!.totalYearToDateReturn, true)
					},
					{
						label: 'Net asset value (NAV)',
						value: fundData.fundAum,
						tooltip: `The net value of the fund's assets less its liabilities as of ${currentDate}.`
					}
				];
				ventureFundDisclaimer.value = `Past performance is no guarantee of future results. Any historical returns, expected returns, or probability projections may not reflect actual future performance. All securities involve risk and may result in partial or total loss. Returns are net of fees.`;
			}
		}
	} finally {
		loading.value = false;
	}
}

setup();
</script>
